import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Grid, Tooltip, Chip, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, Stack, LinearProgress, FormControl, InputLabel, Select, MenuItem, TablePagination } from '@mui/material';
import { Refresh, Cancel, Warning, Upload, LocationOn, Schedule, CheckCircle, ExitToApp, TrendingUp, HourglassEmpty, FilterList, Download } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AttendanceTracker() {
  const [attendance, setAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    absent: 0,
    late: 0,
    earlyArrival: 0,
    earlyLeave: 0,
    overtime: 0,
  });

  // Filter states
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [employeeExpectedTimes, setEmployeeExpectedTimes] = useState({});
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const months = [
    { value: 'all', label: 'All Months' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, []);

  useEffect(() => {
    filterAttendanceData();
  }, [attendance, selectedMonth, selectedYear, employeeExpectedTimes]);

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const filterAttendanceData = () => {
    let filtered = [...attendance];

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(record => {
        const recordYear = record.date.split('-')[0];
        return recordYear === selectedYear;
      });
    }

    // Filter by month
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(record => {
        const recordMonth = record.date.split('-')[1];
        return recordMonth === selectedMonth;
      });
    }

    setFilteredAttendance(filtered);
    calculateStats(filtered);
  };

  const calculateStats = (data) => {
    let absent = 0;
    let late = 0;
    let earlyArrival = 0;
    let earlyLeave = 0;
    let overtime = 0;

    data.forEach(record => {
      const expectedTimes = employeeExpectedTimes[record.employee_id];

      // Count absent
      if (record.status?.toLowerCase() === 'absent') {
        absent++;
        return;
      }

      // Count late - Check both status and actual timing
      if (record.status?.toLowerCase() === 'late') {
        late++;
      } else if (record.in_time && expectedTimes?.loginTime) {
        const expectedMinutes = parseTime(expectedTimes.loginTime);
        const actualMinutes = parseTime(record.in_time);
        if (expectedMinutes !== null && actualMinutes !== null) {
          const difference = actualMinutes - expectedMinutes;
          if (difference > 15) {
            late++;
          }
        }
      }

      // Count early arrivals
      if (record.in_time && expectedTimes?.loginTime) {
        const expectedMinutes = parseTime(expectedTimes.loginTime);
        const actualMinutes = parseTime(record.in_time);
        if (expectedMinutes !== null && actualMinutes !== null) {
          const difference = actualMinutes - expectedMinutes;
          if (difference < -15) {
            earlyArrival++;
          }
        }
      }

      // Count early leaves (only if left early, NOT overtime)
      if (record.out_time && expectedTimes?.logoutTime) {
        const expectedMinutes = parseTime(expectedTimes.logoutTime);
        const actualMinutes = parseTime(record.out_time);
        if (expectedMinutes !== null && actualMinutes !== null) {
          const difference = actualMinutes - expectedMinutes;
          // Early leave: left more than 15 minutes BEFORE expected time
          if (difference < -15) {
            earlyLeave++;
          }
          // Overtime: stayed more than 30 minutes AFTER expected time
          else if (difference > 30) {
            overtime++;
          }
        }
      }
    });

    setStats({ absent, late, earlyArrival, earlyLeave, overtime });
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const company_id = getCompanyIdFromToken();
      const token = localStorage.getItem('token');
      if (!company_id) {
        showNotification('Authentication error. Please log in again.', 'error');
        return;
      }

      const response = await axios.get(`${API_URL}/api/attendance/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAttendance(response.data);

      try {
        const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cmpRes.data && cmpRes.data.data) {
          setCompanyConfig({
            dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
            timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
          });
        }
      } catch(e) {}

      // Extract unique years and months
      const years = [...new Set(response.data.map(r => r.date.split('-')[0]))].sort().reverse();
      const monthsInData = [...new Set(response.data.map(r => r.date.split('-')[1]))].sort();

      setAvailableYears(['all', ...years]);
      setAvailableMonths(monthsInData);

      const uniqueEmployeeIds = [...new Set(response.data.map(r => r.employee_id))];
      const timesMap = {};

      for (const empId of uniqueEmployeeIds) {
        try {
          const timeRes = await axios.get(
            `${API_URL}/api/attendance/expected-times/${empId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          if (timeRes.data.success) {
            timesMap[empId] = timeRes.data.data;
          }
        } catch (err) {
        }
      }
 
      setEmployeeExpectedTimes(timesMap);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showNotification('Failed to fetch attendance records', 'error');
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    if (typeof time === 'number') {
      const hours = Math.floor(time);
      const minutes = Math.round((time - hours) * 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    return time.split(':').slice(0, 2).join(':');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return <CheckCircle />;
      case 'absent': return <Cancel />;
      case 'late': return <Warning />;
      default: return <Schedule />;
    }
  };

  const getTimingIndicator = (record) => {
    const expectedTimes = employeeExpectedTimes[record.employee_id];
    if (!expectedTimes?.loginTime || !record.in_time) {
      return null;
    }

    const expectedMinutes = parseTime(expectedTimes.loginTime);
    const actualMinutes = parseTime(record.in_time);

    if (expectedMinutes === null || actualMinutes === null) return null;

    const difference = actualMinutes - expectedMinutes;

    if (difference < -15) {
      return (
        <Chip
          icon={<TrendingUp />}
          label={`${Math.abs(difference)} min early`}
          color="success"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    } else if (difference > 15) {
      return (
        <Chip
          icon={<Warning />}
          label={`${difference} min late`}
          color="error"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    } else {
      return (
        <Chip
          icon={<CheckCircle />}
          label="On Time"
          color="success"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    }
  };

  const getLeaveTimingIndicator = (record) => {
    const expectedTimes = employeeExpectedTimes[record.employee_id];
    if (!expectedTimes?.logoutTime || !record.out_time) {
      return null;
    }

    const expectedMinutes = parseTime(expectedTimes.logoutTime);
    const actualMinutes = parseTime(record.out_time);

    if (expectedMinutes === null || actualMinutes === null) return null;

    const difference = actualMinutes - expectedMinutes;

    if (difference < -15) {
      return (
        <Chip
          icon={<ExitToApp />}
          label={`${Math.abs(difference)} min early`}
          color="warning"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    } else if (difference > 30) {
      return (
        <Chip
          icon={<HourglassEmpty />}
          label={`${difference} min OT`}
          color="info"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    } else {
      return (
        <Chip
          icon={<CheckCircle />}
          label="On Time"
          color="success"
          size="small"
          sx={{ fontSize: '0.7rem', height: '22px' }}
        />
      );
    }
  };

  const handleUploadDialogOpen = () => {
    setOpenUploadDialog(true);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
  };

  const handleDownloadTemplate = () => {
    // Create CSV content with headers and sample data
    const csvContent = [
      ['employee_id', 'name', 'date', 'in_time', 'out_time', 'status'],
      ['EMP001', 'John Doe', '2025/01/15', '09:00', '17:30', 'Present'],
      ['EMP002', 'Jane Smith', '2025/01/15', '09:15', '18:00', 'Late'],
      ['EMP003', 'Mike Johnson', '2025/01/15', '08:45', '17:15', 'Present']
    ];

    // Convert to CSV string
    const csv = csvContent.map(row => row.join(',')).join('\n');
    
    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'attendance_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showNotification('Template downloaded successfully', 'success');
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExts = ['.xlsx', '.xls', '.csv'];
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validExts.includes(fileExt)) {
        showNotification('Please select a valid Excel file (.xlsx, .xls, .csv)', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadExcel = async () => {
    if (!selectedFile) {
      showNotification('Please select a file to upload', 'error');
      return;
    }

    try {
      setUploading(true);
      const company_id = getCompanyIdFromToken();

      if (!company_id) {
        showNotification('Authentication error. Please log in again.', 'error');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('excelFile', selectedFile);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/attendance/upload-excel`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
           },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      setUploading(false);
      setOpenUploadDialog(false);
      fetchAttendance();

      if (response.data.failed && response.data.failed.length > 0) {
        showNotification(
          `Processed ${response.data.message}, but ${response.data.failed.length} records had errors`,
          'warning'
        );
      } else {
        showNotification(response.data.message, 'success');
      }
    } catch (error) {
      setUploading(false);
      showNotification('Failed to upload Excel file', 'error');
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedAttendance = filteredAttendance.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Attendance Records
          </Typography>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleUploadDialogOpen}
          >
            Upload Excel
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map(month => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {availableYears.map(year => (
                    <MenuItem key={year} value={year}>
                      {year === 'all' ? 'All Years' : year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : (
          <>
           <Grid container spacing={1} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Cancel sx={{ color: '#c62828', mr: 1 }} />
                    <Typography variant="h6">Absent</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {stats.absent}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Warning sx={{ color: '#f57c00', mr: 1 }} />
                    <Typography variant="h6">Late</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {stats.late}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle sx={{ color: '#1565c0', mr: 1 }} />
                    <Typography variant="h6">Early Arrivals</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {stats.earlyArrival}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#fce4ec', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ExitToApp sx={{ color: '#c2185b', mr: 1 }} />
                    <Typography variant="h6">Early Departure</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {stats.earlyLeave}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ color: '#2e7d32', mr: 1 }} />
                    <Typography variant="h6">Overtime</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold' }}>
                    {stats.overtime}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Attendance Table - Optimized Layout */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Check In</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Time In Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>In Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Check Out</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Time Out Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Out Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAttendance.map((record, index) => {
                    const expectedTimes = employeeExpectedTimes[record.employee_id];
                    return (
                      <TableRow key={index} hover>
                        {/* Merged Employee ID & Name Column */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {record.employee_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {record.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{dayjs(record.date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}</TableCell>
                        {/* Check In Time Column */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {formatTime(record.in_time)}
                            </Typography>
                            {expectedTimes?.loginTime && (
                              <Typography variant="caption" color="text.secondary">
                                Expected: {formatTime(expectedTimes.loginTime)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* In Status */}
                        <TableCell>
                          {getTimingIndicator(record)}
                        </TableCell>
                        {/* In Location */}
                        <TableCell>
                          {record.in_latitude && record.in_longitude ? (
                            <Tooltip title="View on Map">
                              <Chip
                                icon={<LocationOn />}
                                label={`${record.in_latitude.toFixed(4)}, ${record.in_longitude.toFixed(4)}`}
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        {/* Check Out Time Column */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {formatTime(record.out_time)}
                            </Typography>
                            {expectedTimes?.logoutTime && (
                              <Typography variant="caption" color="text.secondary">
                                Expected: {formatTime(expectedTimes.logoutTime)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {/* Out Status */}
                        <TableCell>
                          {getLeaveTimingIndicator(record)}
                        </TableCell>
                        {/* Out Location */}
                        <TableCell>
                          {record.out_latitude && record.out_longitude ? (
                            <Tooltip title="View on Map">
                              <Chip
                                icon={<LocationOn />}
                                label={`${record.out_latitude.toFixed(4)}, ${record.out_longitude.toFixed(4)}`}
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        {/* Overall Status */}
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(record.status)}
                            label={record.status}
                            color={getStatusColor(record.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredAttendance.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </>
        )}

        {/* Upload Dialog */}
        <Dialog open={openUploadDialog} onClose={handleUploadDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Attendance Excel File</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              Please select an Excel file (.xlsx, .xls, .csv) containing attendance records.
              The file should have the following columns: employee_id, name, date, in_time, out_time, status.
            </DialogContentText>

            <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                ⚠️ Note:
              </Typography>
              <Typography variant="body2" component="div">
                * employee_id must match an existing ID in the employee details<br />
                * name should be the full name of the employee<br />
                * Date format: DD/MM/YYYY (e.g., 15/02/2025)<br />
                * Time format: HH:MM (24-hour format, e.g., 09:30, 14:45)<br />
                * Status: Present, Absent, Late, or leave blank for auto-calculation<br />
                * Status will be automatically calculated based on role's login time if not provided<br />
                * Late threshold: 15 minutes after expected login time
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadTemplate}
              fullWidth
              sx={{ mb: 2 }}
            >
              Download Template
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={<Upload />}
              fullWidth
              disabled={uploading}
            >
              Select File
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
              />
            </Button>

            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="body2">
                  Selected file: {selectedFile.name}
                </Typography>
              </Box>
            )}

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  Uploading: {uploadProgress}%
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUploadDialogClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadExcel}
              variant="contained"
              disabled={!selectedFile || uploading}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}

export default AttendanceTracker;